import { Box, Menu, Text } from "@mantine/core";
import { useAtom } from "jotai";
import { useTranslation } from "react-i18next";

import { useAccess } from "~/access/useAccess";
import { isAdminViewAtom } from "~/atoms";
import { Center, Chip } from "~/components";
import SvgIconRaw from "~/components/SvgIconRaw";
import { useSemanticColors } from "~/utils/Theme/useSemanticColors";

import RoundIconButton from "../Button/RoundIconButton";

const SwichView = () => {
  const { t } = useTranslation("common");

  const { payload } = useAccess();
  const [isAdminView, setIsAdminView] = useAtom(isAdminViewAtom);
  const colors = useSemanticColors();

  // A lens the payload does not let the person toggle is settled by AccessGate.
  if (!payload?.lens.canToggle) {
    return null;
  }

  // Both states dim in dark mode: the light-mode tint shades (primary-0/-2,
  // warning-50/error-200) glow against the dark nav, so dark mode uses the
  // muted alpha "light" variant instead.
  const chipLabelColor = isAdminView ? "primary" : "error";
  const chipTextColor = isAdminView
    ? "light-dark(var(--mantine-primary-color-filled), var(--mantine-primary-color-light-color))"
    : `light-dark(${colors.myfg.errorPrimary}, var(--mantine-color-red-light-color))`;
  const chipBgColor = isAdminView
    ? "light-dark(var(--mantine-primary-color-0), var(--mantine-primary-color-light))"
    : `light-dark(${colors.utility.warning[50]}, var(--mantine-color-red-light))`;
  const chipBorderColor = isAdminView
    ? "light-dark(var(--mantine-primary-color-2), var(--mantine-primary-color-light))"
    : `light-dark(${colors.utility.error[200]}, var(--mantine-color-red-light))`;
  const expandMoreIcon = isAdminView
    ? "light-dark(var(--mantine-primary-color-filled), var(--mantine-primary-color-light-color))"
    : "light-dark(var(--mantine-color-fg-error-primary), var(--mantine-color-red-light-color))";

  const getCurrentViewLabel = () => {
    if (isAdminView) return t("nav.adminView");
    return t("nav.producerView");
  };

  const getSwitchToViewLabel = () => {
    if (!isAdminView) return t("nav.switchToAdminView");
    return t("nav.switchToProducerView");
  };

  return (
    <Center direction="row">
      <Menu position="bottom-end" offset={8} shadow="md">
        <Menu.Target>
          <Box>
            <Chip
              label={
                <Center direction="row" gap={4}>
                  <Text size="xs">{getCurrentViewLabel()}</Text>
                  <RoundIconButton size={16}>
                    <SvgIconRaw
                      name="ChevronDown"
                      size={24}
                      color={expandMoreIcon}
                    />
                  </RoundIconButton>
                </Center>
              }
              color={chipLabelColor}
              onClick={() => undefined}
              size="small"
              style={{
                color: chipTextColor,
                backgroundColor: chipBgColor,
                borderColor: chipBorderColor,
              }}
              data-testid="view-switcher"
            />
          </Box>
        </Menu.Target>
        <Menu.Dropdown miw={200}>
          <Menu.Item
            onClick={() => setIsAdminView(!isAdminView)}
            data-testid="view-switch-option"
          >
            <Text size="xs" fw={600}>
              {getSwitchToViewLabel()}
            </Text>
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Center>
  );
};

export default SwichView;
